import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import axios from '../utils/axios';

const PAGE_SIZE = 100;

export default function OzonSplit() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    async function loadAccounts() {
      try {
        const { data } = await axios.get('/mswb/ozon/orders/accounts');
        if (!cancelled) setAccounts(Array.isArray(data) ? data : []);
      } catch (_) {
        // silent
      }
    }
    loadAccounts();
    return () => { cancelled = true; };
  }, []);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  const allSelected = useMemo(() => {
    if (!pagedItems.length) return false;
    return pagedItems.every(it => selected.has(it.posting_number));
  }, [pagedItems, selected]);

  const indeterminate = useMemo(() => {
    if (!pagedItems.length) return false;
    const count = pagedItems.filter(it => selected.has(it.posting_number)).length;
    return count > 0 && count < pagedItems.length;
  }, [pagedItems, selected]);

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) {
      for (const it of pagedItems) next.delete(it.posting_number);
    } else {
      for (const it of pagedItems) next.add(it.posting_number);
    }
    setSelected(next);
  };

  const toggleOne = (pn) => {
    const next = new Set(selected);
    if (next.has(pn)) next.delete(pn); else next.add(pn);
    setSelected(next);
  };

  const handleLoad = async () => {
    if (!accountId) { setError('Выберите аккаунт OZON'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.get('/mswb/ozon/orders/multi-qty', { params: { account_id: accountId } });
      const arr = Array.isArray(data?.items) ? data.items : [];
      setItems(arr);
      setSelected(new Set());
      setPage(1);
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleSplit = async () => {
    if (!accountId) { setError('Выберите аккаунт OZON'); return; }
    const selectedItems = items.filter(it => selected.has(it.posting_number));
    if (!selectedItems.length) { setError('Выберите хотя бы один постинг'); return; }
    const postings = selectedItems.map(it => ({
      posting_number: it.posting_number,
      products: (it.products || []).map(p => ({ sku: p.sku, quantity: p.quantity })),
    }));
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/mswb/ozon/orders/split', { account_id: Number(accountId), postings });
      const results = Array.isArray(data?.results) ? data.results : [];
      setReport(results);
      setReportOpen(true);
    } catch (e) {
      setReport([{ posting_number: '-', ok: false, message: e?.response?.data?.message || 'Ошибка разбивки' }]);
      setReportOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Ozon: Разбивка отправлений
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel>Аккаунт OZON</InputLabel>
            <Select
              value={accountId}
              label="Аккаунт OZON"
              onChange={(e) => setAccountId(e.target.value)}
            >
              {accounts.map(acc => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name || `Аккаунт ${acc.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleLoad} disabled={loading}>
            Загрузить
          </Button>
          <Button variant="outlined" onClick={handleSplit} disabled={loading || selected.size === 0}>
            Разбить выбранные
          </Button>
          <Typography variant="body2" color="text.secondary">
            Выбрано: {selected.size} из {items.length}
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={indeterminate}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>Заказ</TableCell>
              <TableCell>Товары</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedItems.map((it) => (
              <TableRow key={it.posting_number} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(it.posting_number)}
                    onChange={() => toggleOne(it.posting_number)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{it.posting_number}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(it.products || []).map((p, idx) => (
                      <Chip
                        key={idx}
                        label={`${String(p.offer_id || '-').replace(/_/g, ' ')} ${p.quantity}шт`}
                        size="small"
                        sx={{ bgcolor: 'action.selected' }}
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Нет данных — выберите аккаунт и нажмите «Загрузить»
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => { setPage(val); setSelected(new Set()); }}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Результат разбивки</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(report || []).map((r, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: r.ok ? 'success.lighter' : 'error.lighter',
                  border: 1,
                  borderColor: r.ok ? 'success.light' : 'error.light',
                }}
              >
                <Typography variant="body2" fontWeight={600}>{r.posting_number}</Typography>
                <Typography variant="body2" color={r.ok ? 'success.dark' : 'error.dark'}>
                  {r.ok ? 'OK' : (r.message || 'Ошибка')}
                </Typography>
              </Box>
            ))}
            {!report?.length && (
              <Typography variant="body2" color="text.secondary">Пусто</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
